import { useAppTheme } from "@hooks/useAppTheme";
import type { WorkflowItemNode } from "@memorang/types/src/session/new-session";
import { Handle, type NodeProps, Position, useReactFlow } from "@xyflow/react";
import { TextInput } from "react-native";
import Shape, { type ShapeType } from "./Shape";

const StandardNode = ({
	id,
	label,
	width = 200,
	color,
	textColor,
	height = 50,
	type,
	correct,
}: {
	id: string;
	label: string;
	height?: number;
	width?: number;
	color: string;
	textColor: string;
	type: ShapeType;
	correct?: boolean;
}) => {
	const { updateNodeData } = useReactFlow();
	const theme = useAppTheme();

	const handleLabelChange = (text: string) => {
		updateNodeData(id, { label: text });
	};

	return (
		<>
			<Shape
				type={type}
				width={width || 180}
				height={height || 50}
				fill={correct === false ? theme.colors.error : color}
				strokeWidth={2}
				stroke={correct === false ? theme.colors.error : textColor}
				fillOpacity={0.8}
			/>
			<TextInput
				multiline
				editable={correct === undefined}
				value={label}
				onChangeText={handleLabelChange}
				placeholder="Add a label"
				placeholderTextColor={theme.colors.outlineVariant}
				style={{
					maxWidth: (width ?? 0) - 20,
					padding: 8,
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					textAlign: "center",
					outline: "none",
					...theme.fonts.labelMedium,
					fontSize: 10,
					color: correct === false ? theme.colors.white : textColor,
				}}
			/>
		</>
	);
};

export const InputNode = ({ id, data, width }: NodeProps<WorkflowItemNode>) => {
	const theme = useAppTheme();
	const { label, correct } = data;

	return (
		<>
			<StandardNode
				type="rectangle"
				id={id}
				label={label}
				width={width ?? 0}
				height={50}
				color={theme.colors.surface}
				textColor={theme.colors.onSurface}
				correct={correct}
			/>
			<Handle type="source" position={Position.Bottom} />
		</>
	);
};

export const DefaultNode = ({
	id,
	data,
	width,
}: NodeProps<WorkflowItemNode>) => {
	const { label, correct } = data;
	const theme = useAppTheme();

	return (
		<>
			<StandardNode
				type="rectangle"
				id={id}
				label={label}
				width={width ?? 0}
				height={50}
				color={theme.colors.secondaryContainer}
				textColor={theme.colors.onSecondaryContainer}
				correct={correct}
			/>
			<Handle type="target" position={Position.Top} />
			<Handle type="source" position={Position.Bottom} />
		</>
	);
};

export const OutputNode = ({
	id,
	data,
	width,
}: NodeProps<WorkflowItemNode>) => {
	const { label, correct } = data;
	const theme = useAppTheme();

	return (
		<>
			<StandardNode
				type="rectangle"
				id={id}
				label={label}
				width={width ?? 0}
				height={50}
				color={theme.colors.surface}
				textColor={theme.colors.onSurface}
				correct={correct}
			/>
			<Handle type="target" position={Position.Top} />
		</>
	);
};

export function DecisionNode({
	id,
	data,
	width,
	height,
}: NodeProps<WorkflowItemNode>) {
	const theme = useAppTheme();
	const { label, correct } = data;

	return (
		<>
			<StandardNode
				type="diamond"
				id={id}
				label={label}
				width={width}
				height={height}
				color={theme.colors.primary}
				textColor={theme.colors.onPrimary}
				correct={correct}
			/>
			<Handle type="target" position={Position.Top} />
			<Handle type="source" position={Position.Left} id="positive" />
			<Handle type="source" position={Position.Right} id="negative" />
		</>
	);
}
